import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { DomSanitizer, type SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule } from '@ngx-translate/core';
import { PortalService } from '../../../services/portal.service';

export interface PreviewDocument {
  name: string;
  storageUrl: string;
  mimeType: string;
  category?: string;
  fileSize?: number;
  createdAt?: string;
}

@Component({
  selector: 'om-document-preview',
  imports: [TranslateModule],
  templateUrl: './document-preview.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentPreview {
  private readonly portal = inject(PortalService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly isOpen = input(false);
  readonly document = input<PreviewDocument | null>(null);
  readonly categoryLabel = input('');

  readonly closed = output<void>();

  readonly portalRef = viewChild<ElementRef<HTMLElement>>('previewPortal');

  readonly isPdf = computed(() => this.document()?.mimeType === 'application/pdf');
  readonly safeUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.document()?.storageUrl;
    if (!url || !url.includes('firebasestorage.googleapis.com')) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });

  constructor() {
    this.portal.attach(this.portalRef, this.isOpen, inject(DestroyRef));
  }

  formatFileSize(bytes?: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  onClose(): void {
    this.closed.emit();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.onClose();
  }
}
